import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, Sector } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';

const AlertLevelChart = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const { theme, getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  const COLORS = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
  };

  const chartData = [
    { name: 'Low Alert', value: data.low, color: COLORS.low },
    { name: 'Medium Alert', value: data.medium, color: COLORS.medium },
    { name: 'High Alert', value: data.high, color: COLORS.high },
  ];

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const textColor = theme === 'light' ? '#1f2937' : '#ffffff';
    const secondaryTextColor = theme === 'light' ? '#6b7280' : '#9ca3af';
    const accentColor = theme === 'light' ? '#059669' : '#4ade80';

    return (
      <g>
        <text x={cx} y={cy - 10} dy={8} textAnchor="middle" fill={textColor} className="font-bold text-lg">
          {payload.name}
        </text>
        <text x={cx} y={cy + 15} dy={8} textAnchor="middle" fill={secondaryTextColor} className="text-sm">
          {`${value} vehicles`}
        </text>
        <text x={cx} y={cy + 35} dy={8} textAnchor="middle" fill={accentColor} className="text-sm font-semibold">
          {`${(percent * 100).toFixed(1)}%`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 15}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke={theme === 'light' ? '#f3f4f6' : '#ffffff'}
          strokeWidth={2}
          style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
        />
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const tooltipBg = theme === 'light' ? 'bg-white/95 border border-gray-300 shadow-lg' : 'bg-gray-800/90';
      const textPrimary = theme === 'light' ? 'text-gray-900' : 'text-white';
      const textSecondary = theme === 'light' ? 'text-gray-700' : 'text-gray-100';
      
      return (
        <div className={`${tooltipBg} rounded-lg p-4 backdrop-blur-sm`}>
          <p className={`${textPrimary} font-bold text-lg mb-2`} style={{ textShadow: 'none' }}>{payload[0].name}</p>
          <p className={`${textSecondary} text-base mb-1`} style={{ textShadow: 'none' }}>
            Vehicles: <span className={`${textPrimary} font-bold text-lg`}>{payload[0].value}</span>
          </p>
          <p className={`${textSecondary} text-base`} style={{ textShadow: 'none' }}>
            Percentage: <span className={`${textPrimary} font-bold text-lg`}>{((payload[0].value / (data.low + data.medium + data.high)) * 100).toFixed(1)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  return (
    <div className={`${themeClasses.chartBackground} border ${themeClasses.border} rounded-xl p-3 sm:p-4 lg:p-6 hover:border-military-500/70 hover:scale-102 hover:shadow-xl transform transition-all duration-300 cursor-pointer`}>
      <h3 className={`text-base sm:text-lg lg:text-xl font-bold ${themeClasses.textPrimary} mb-3 sm:mb-4 transition-colors duration-300 hover:text-military-600`}>Fleet Health by Alert Level</h3>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={activeIndex === null ? ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%` : false}
            outerRadius={110}
            fill="#8884d8"
            dataKey="value"
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                opacity={activeIndex === null || activeIndex === index ? 1 : 0.3}
                stroke={activeIndex === index ? "#ffffff" : "transparent"}
                strokeWidth={activeIndex === index ? 3 : 0}
                style={{ 
                  transition: 'all 0.3s ease',
                  filter: activeIndex === index ? 'brightness(1.2) drop-shadow(0 2px 4px rgba(0,0,0,0.4))' : 'none'
                }}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AlertLevelChart;
